import Vue from 'vue';
import store from '@/scripts/store';
// Vue Plugins
import AsyncComputed from 'vue-async-computed';
import VModal from 'vue-js-modal';
import VTooltip from 'v-tooltip';
import VueScrollTo from 'vue-scrollto';

import camelCase from 'lodash/camelCase';
import kebabCase from 'lodash/kebabCase';

import analytic from '@/scripts/libs/analytic';

import elDataHelper from '@/scripts/helpers/element-data-helper';
import stringHelper from '@/scripts/helpers/string-helper';
import DATA_NAMESPACE from './namespace';
import { TranslationSupport } from './translation-support';
import { configureSsrData } from './ssrDataLoader';

import { initializeSentry } from '@/scripts/services/sentry';
import * as Integrations from '@sentry/integrations';

// Load Vue plugins
Vue.use(AsyncComputed);
Vue.use(VTooltip);
Vue.use(VModal, {
  dialog: true,
  dynamic: true,
});
Vue.use(VueScrollTo, {
  offset: -58,
});

// Load sentry integration
initializeSentry({
  integrations: integrations => [
    ...integrations,
    new Integrations.Vue({ Vue }),
  ],
});

const getModuleName = function geModuleName(pageName) {
  return `VS${pageName}`;
};

/**
 * Create a Page object
 * @param {Element} rootElement
 */
const Page = function Page(rootElement) {
  this.root = rootElement;
  this.name = elDataHelper.getValue(this.root, DATA_NAMESPACE.page);
  this.moduleName = getModuleName(
    stringHelper.capitalize(
      stringHelper.toCamelCase(this.name),
    ),
  );
  this.module = null;

  this.translationSupport = new TranslationSupport(I18n);
};

Page.prototype.setModule = function setModule(pageModule) {
  this.module = pageModule.default;
};

/**
 * Emulate vue props passing mechanism for static template file.
 * This function will pass the props to the Vue instance during its creation.
 * Note: For simplicity, we only want to support JavaScript Object and string.
 */
Page.prototype.getPropsData = function getPropsData(vueObject) {
  // Check the type of props and return an array of props key
  const arrayOfPropKeys = (
    Array.isArray(vueObject.props)
      ? vueObject.props
      : Object.keys(vueObject.props)
  );

  // Get dynamic binding key
  const dynamicAttributes = [...(this.root.attributes || [])]
    .filter(attribute => (attribute.name || '')[0] === ':')
    .reduce((acc, attribute) => {
      const key = camelCase(attribute.name.substring(1));
      acc[key] = attribute.value;
      return acc;
    }, {})
  ;

  // Initiaize ssr_data object
  const ssrData = configureSsrData();

  // Parse and assign each of the prop keys
  return arrayOfPropKeys
    .map(key => ({
      isStatic: !(key in dynamicAttributes),
      kebabKey: kebabCase(key),
      camelKey: camelCase(key),
    }))
    .map(({ isStatic, kebabKey, camelKey }) => {
      // Handle static props
      if (isStatic) {
        const domAttributeAccessor = this.root.hasAttribute(kebabKey) ? kebabKey : camelKey;
        const propValue = this.root.getAttribute(domAttributeAccessor);
        return {
          domAttributeAccessor,
          propKey: camelKey,
          propData: propValue,
        };
      }

      // Handle dynamic props
      const boundKeyValue = dynamicAttributes[camelKey];
      const domAttributeAccessor = this.root.hasAttribute(`:${kebabKey}`) ? `:${kebabKey}` : `:${camelKey}`;

      if (boundKeyValue in ssrData) {
        return {
          domAttributeAccessor,
          propKey: camelKey,
          propData: ssrData[boundKeyValue],
        };
      }
    })
    .filter(x => x)
  ;
};

Page.prototype.init = function init() {
  if (!this.module) throw new Error('No module is set for this page yet.');
  const vueObject = this.module.init();
  const componentOptions = vueObject.options || vueObject;

  // Set Vue mounting point to the element root
  if (!componentOptions.el) componentOptions.el = this.root;

  // Include additional vuex property
  componentOptions.store = store;
  componentOptions.name = this.name;

  // Handle vue props
  if (componentOptions.props) {
    componentOptions.propsData = {};
    this.getPropsData(componentOptions)
      .forEach(({ domAttributeAccessor, propKey, propData }) => {
        componentOptions.propsData[propKey] = propData;
        this.root.removeAttribute(domAttributeAccessor);
      })
    ;
  }

  const translationSupport = this.translationSupport;
  Vue.mixin({
    mixins: [
      translationSupport.setup('$t'),
    ],
    created() {
      this.analytic = analytic;
    },
  });

  // Create the vue instance
  this.app = new Vue(vueObject);
};

export default Page;
